const modelGlobalSetting = {
  splitterSize: 5,
  splitterExtra: 4,
  tabEnableRename: false,
  //tabEnableFloat: true,
  tabSetEnableClose: true,
  tabSetMinWidth: 100,
  tabSetMinHeight: 100,
  tabSetTabStripHeight: 40,
  //borderMinSize: 100,
  borderEnableAutoHide: true
  //tabEnableClose: true
}

const validationMessage = {
  workspaceName: {
    empty: 'Workspace name cannot be empty',
    exists: 'Workspace name already exists'
  }
}

const modelAppConfig = {
  type: 'tab',
  name: '',
  component: 'web-app', //put web-player for Spotfire dashboards
  icon: '',
  config: {
    type: 'url',
    data: ''
  }
}

const modelStructure = {
  global: modelGlobalSetting,
  borders: [],
  layout: {
    type: 'row',
    weight: 100,
    children: [
      {
        type: 'tabset',
        weight: 100,
        children: [modelAppConfig],
        active: true
      }
    ]
  }
}

const defaultWorkspaceName = 'Primary Workspace',
  defaultWorkspaceId = 'my-workspace'

const smartflowWorkspace = { id: 'smart-flow', name: 'Smartflow Workspace' }

const workspaceSchema = {
  id: defaultWorkspaceId,
  name: defaultWorkspaceName,
  icon: 'workspace',
  model: []
}

const getLayoutModel = (apps, appIndex) => {
  const layoutModel = {
    ...modelStructure,
    layout: {
      ...modelStructure.layout,
      children: [
        {
          ...modelStructure.layout.children[0],
          children: apps.length > 0 ? getAppLayoutArray(apps) : [],
          ...(appIndex ? { selected: appIndex } : {})
        }
      ]
    }
  }
  return layoutModel
}

const getMicroAppLayout = (app) => {
  return {
    ...modelAppConfig,
    name: `${app.application}-${app.task}`,
    icon: null,
    component: 'microapp',
    config: { type: 'qna', data: app }
  }
}

const isNumeric = (str) => {
  if (typeof str !== 'string') return false // we only process strings!
  return (
    !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !isNaN(parseFloat(str))
  ) // ...and ensure strings of whitespace fail
}

const getAppLayoutArray = (apps) => {
  return apps.map((app) => {
    return {
      ...modelAppConfig,
      name: app.name,
      icon: app?.appIcon?.url,
      config: { ...modelAppConfig.config, data: app.url }
    }
  })
}

const getUniqueAppName = (appName) => {
  return appName.replace(/[^a-zA-Z0-9]/g, '-')
}

// Depth-first search for the first tabset node in a flexlayout layout tree.
const findFirstTabset = (node) => {
  if (!node || typeof node !== 'object') return null
  if (node.type === 'tabset') return node
  const children = Array.isArray(node.children) ? node.children : []
  for (const child of children) {
    const found = findFirstTabset(child)
    if (found) return found
  }
  return null
}

/**
 * Append the supplied apps (as tabs) to an existing workspace layout model.
 * Falls back to building a fresh layout model when no valid layout is present.
 * Apps are expected to carry `name`, `appIcon.url`/`icon` and `url`/`config.data`.
 */
const appendAppsToModel = (model, apps) => {
  if (!apps || apps.length === 0) return model
  const hasLayout = model && !Array.isArray(model) && model.layout
  if (!hasLayout) {
    return getLayoutModel(apps)
  }
  const clone = JSON.parse(JSON.stringify(model))
  const tabset = findFirstTabset(clone.layout)
  if (tabset) {
    const appTabs = getAppLayoutArray(apps)
    tabset.children = [...(Array.isArray(tabset.children) ? tabset.children : []), ...appTabs]
    tabset.active = true
  }
  return clone
}

// Build a tabset node holding the supplied tab children.
const makeTabset = (children, active) => ({
  type: 'tabset',
  weight: 100,
  children,
  ...(active ? { active: true } : {})
})

// Split an array into `n` sequential, roughly-even groups (order preserved).
const chunkEvenly = (arr, n) => {
  const size = Math.ceil(arr.length / n) || 1
  const groups = []
  for (let i = 0; i < n; i++) groups.push(arr.slice(i * size, (i + 1) * size))
  return groups
}

/**
 * Build a flexlayout layout tree for a given pane arrangement, distributing the
 * supplied apps across the panes. Variants mirror the Create-workspace wizard:
 *  - 'single'     : one pane with all apps as tabs
 *  - 'columns'    : two side-by-side panes
 *  - 'left-stack' : a primary pane on the left and remaining apps stacked on the right
 *  - 'grid'       : a 2x2 quadrant of panes
 * The server only stores each app's position (not its pane), so apps are
 * distributed by order; the pane structure itself is faithfully reproduced.
 */
const buildVariantLayout = (apps, variant) => {
  const tabs = getAppLayoutArray(apps || [])
  switch (variant) {
    case 'columns': {
      const [left, right] = chunkEvenly(tabs, 2)
      return { type: 'row', weight: 100, children: [makeTabset(left, true), makeTabset(right)] }
    }
    case 'left-stack': {
      const [primary, ...rest] = tabs
      const stack = rest.length ? rest.map((tab) => makeTabset([tab])) : [makeTabset([])]
      return {
        type: 'row',
        weight: 100,
        children: [
          makeTabset(primary ? [primary] : [], true),
          { type: 'row', weight: 100, children: stack }
        ]
      }
    }
    case 'grid': {
      const [g0, g1, g2, g3] = chunkEvenly(tabs, 4)
      return {
        type: 'row',
        weight: 100,
        children: [
          { type: 'row', weight: 100, children: [makeTabset(g0, true), makeTabset(g1)] },
          { type: 'row', weight: 100, children: [makeTabset(g2), makeTabset(g3)] }
        ]
      }
    }
    case 'single':
    default:
      return { type: 'row', weight: 100, children: [makeTabset(tabs, true)] }
  }
}

// Build a full flexlayout model for the given apps arranged per the layout variant.
const getLayoutModelForVariant = (apps, variant) => ({
  ...modelStructure,
  layout: buildVariantLayout(apps, variant)
})

export {
  modelStructure,
  modelAppConfig,
  workspaceSchema,
  defaultWorkspaceName,
  defaultWorkspaceId,
  getLayoutModel,
  getLayoutModelForVariant,
  isNumeric,
  validationMessage,
  getUniqueAppName,
  smartflowWorkspace,
  getMicroAppLayout,
  appendAppsToModel
}
